import React, { useCallback, useMemo, useRef } from 'react';

import { UserProfileNumbersTeamsSectionNumbersProps } from './Numbers.section.decl';

import { Box, Icon, Spacer, Tag, Typography, useTheme } from '@aircall/tractor';
import { Paper } from '@components/Paper/Paper';
import { DataTable } from '@components/Table/DataTable';
import { CellRenderProps, ColumnProps } from '@components/Table/DataTable.decl';
import { TableDropdown } from '@components/Table/TableDropdown';
import { NUMBER_DETAIL_ROUTE } from '@constants/routes.constants';
import { routeReplacer } from '@dashboard/library';
import { GetUserAssociatedLinesQuery_getAgentV2_associatedLines_items as UserAssociatedLines } from '@generated/GetUserAssociatedLinesQuery';
import { getFlagName } from '@helpers/countries.helpers';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useInfiniteLoader } from '@hooks/useInfiniteLoader';
import { useUpdateDefaultNumber } from '@hooks/useUpdateDefaultNumber';
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const NumbersSection = React.memo(
  ({ user, associatedLines, fetchMore }: UserProfileNumbersTeamsSectionNumbersProps) => {
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const { currentUser } = useGlobalData();
    const { t } = useTranslation();
    const { space } = useTheme();
    const navigate = useNavigate();

    const isOwnUser = currentUser.ID === user.ID;

    // handles mutation for set default numbers
    const { onUpdateDefaultNumber } = useUpdateDefaultNumber(user, isOwnUser);

    /** Columns definitions for the table */
    const tableColumns: ColumnProps<UserAssociatedLines>[] = useMemo(() => {
      const nameCellRenderer = ({ datum }: CellRenderProps<UserAssociatedLines>) => (
        <Typography ellipsis>{datum?.name}</Typography>
      );

      const numberCellRenderer = ({ datum }: CellRenderProps<UserAssociatedLines>) => {
        const numberId = datum.ID;
        const userNumber = parsePhoneNumber(
          datum.phoneNumber,
          datum.countryISOCode as CountryCode
        ).number;
        const flagName = getFlagName(datum.countryISOCode);

        return (
          <Spacer space='xxs' alignItems='center'>
            <Icon component={flagName} size={space.m} />
            <Spacer space='xxl' alignItems='center'>
              <Typography>{userNumber}</Typography>
              {user.defaultOutPhoneNumberID && numberId === user.defaultOutPhoneNumberID && (
                <Tag variant='primary' size='small'>
                  {t('user_profile.numbers_and_teams.associated_numbers.tags.default_number')}
                </Tag>
              )}
            </Spacer>
          </Spacer>
        );
      };

      const optionsCellRenderer = ({ datum, rowIndex }: CellRenderProps<UserAssociatedLines>) => {
        const numberId = datum.ID;

        return (
          <TableDropdown
            options={[
              {
                title: t('user_profile.numbers_and_teams.associated_numbers.tags.set_as_default'),
                callback: () => onUpdateDefaultNumber(numberId),
              },
              {
                title: t(
                  'user_profile.numbers_and_teams.associated_numbers.tags.go_to_number_page'
                ),
                callback: () => {
                  navigate(routeReplacer(NUMBER_DETAIL_ROUTE, { numberId }));
                },
              },
            ]}
            data-test={`associated-numbers-table-dropdown-${rowIndex}`}
          />
        );
      };

      return [
        {
          name: t('user_profile.numbers_and_teams.associated_numbers.table.columns.name.title'),
          width: '304px',
          cellRenderer: nameCellRenderer,
        },
        {
          name: t('user_profile.numbers_and_teams.associated_numbers.table.columns.number.title'),
          index: 'number',
          cellRenderer: numberCellRenderer,
        },
        {
          name: '',
          width: '40px',
          cellRenderer: optionsCellRenderer,
        },
      ];
    }, [navigate, onUpdateDefaultNumber, space.m, t, user.defaultOutPhoneNumberID]);

    const { nextToken, currentToken } = associatedLines.pageInfo;
    const itemsCount = associatedLines.items.length;

    const handleLoad = useCallback(() => {
      if (!(itemsCount && nextToken && currentToken !== nextToken)) {
        return;
      }

      fetchMore({
        variables: { agentID: user.ID, token: nextToken },
      });
    }, [currentToken, fetchMore, itemsCount, nextToken, user]);

    useInfiniteLoader({
      containerRef: scrollableContainerRef,
      threshold: 900,
      skip: false,
      onLoad: handleLoad,
    });

    return (
      <Paper title={t('user_profile.numbers_and_teams.associated_numbers.title')}>
        <Box>
          <DataTable
            tableBodyRef={scrollableContainerRef}
            data={associatedLines.items}
            data-test='associated-numbers-table'
            emptyMessage={t('user_profile.numbers_and_teams.associated_numbers.table.empty')}
            columns={tableColumns}
          />
        </Box>
      </Paper>
    );
  }
);
